import Vue from 'vue';
import Router, { Route, RouteConfig } from 'vue-router';
import AuthService from '@/modules/user/AuthService';
import { QuizCategory } from '@/models/Quiz';
import { oneOffStatementsConfig } from '@/modules/Statements/services/oneOffStatements';
import EnvironmentService from '@/env/EnvironmentService';
import DictionaryStore from '@/commons/services/store/DictionaryStore';
import ClientAuthService from '@/modules/client/services/ClientAuthService';
import { Routes } from './Routes';
import { NotificationScope } from '@/modules/notifications/models/NotificationDescriptor';
import PartnerRoutes from '@/modules/partner/services/PartnerRoutes';
import LoanApplicationsRoutes from '@/modules/loanApplication/services/LoanApplicationsRoutes';
import RegulationRoutes from '@/modules/regulation/services/RegulationRoutes';
import AccreditationPanelRoutes from '@/modules/permissions/services/accreditationPanelRoutes';
import DealsRoutes from '@/modules/deals/services/DealsRoutes';
import ReportRoutes from '@/modules/report/services/ReportRoutes';
import FilemanagerRoutes, { FilemanagerFileRoute } from '@/modules/filemanager/FilemanagerRoutes';
import ProceduresRoutes from '@/modules/procedures/ProceduresRoutes';
import IssuesRoutes from '@/modules/issues/IssuesRoutes';
import DiscussionsRoutes from '@/modules/discussions/DiscussionsRoutes';
import ProspectsRoutes from '@/modules/prospects/services/ProspectsRoutes';
import { clone } from 'lodash-es';
import TrainingsRoutes from '@/modules/trainings/TrainingsRoutes';
import AdminUserPanelRoutes from '@/modules/adminPanel/services/AdminUserPanelRoutes';
import DelegationsListsRoutes from '@/modules/adminPanel/services/DelegationsListsRoutes';
import ClientRoutes from '@/modules/client/services/ClientRoutes';
import BhpRoutes from '@/modules/bhp/services/BhpRoutes';
import KnfRoutes from '@/modules/knf/services/KnfRoutes';
import WidgetRoutes from '@/modules/widgets/services/WidgetRoutes';
import LivespaceRoutes from '@/modules/livespace/services/LivespaceRoutes';
import AccreditationRoutes from '@/modules/accredition/services/AccreditationRoutes';
import HumanResourcesRoutes from '@/modules/adminPanel/services/HumanResourcesRoutes';
// Navigation Wrapper musi zostać w main chunku z uwagi na post build
import NavigationWrapper from '@/modules/NavigationWrapper.vue';
import CalculatorRoutes from '@/components/calculator/CalculatorRoutes';
import { EmptyTemplateWithRouterView } from '@/router/templates';
import BranchesRoutes from '@/modules/branches/services/BranchRoutes';
import AdminDivisionPanelRoutes from '@/modules/adminPanel/services/AdminDivisionPanelRoutes';
import UserApi from '@/modules/user/services/UserApi';

import { I18NGetter } from '@/services/enumTranslator/I18NGetter';
import { DashboardKey } from '@/modules/onboarding/tours/enums/DashboardKey';
import { BikKey } from '@/modules/onboarding/tours/enums/BikKey';
import { PrintoutBeforeEnter } from '@/components/calculator/printout/PrintoutBeforeEnter';
import NPSRoutes from '@/modules/nps/services/NPSRoutes';
import { IUser } from '@/models/user/IUser';
import { Quizzes } from '@/models/user/Quizzes';
import DivisionRoutes from '@/modules/division/DivisionRoutes';
import { CrmModules } from '@/types/CrmModules';
import { ProvisionsRoutes } from '@/modules/provisions/ProvisionsRoutes';
import useModuleGuardService from '@/router/UseModuleGuardService';
import { canAccessDivisionReports, divisionReportsGuard } from '@/modules/divisionReports/services/DivisionReportsAccess';
import StatementRoutes from '@/modules/Statements/services/statementRoutes';
import BankStmtRoutes from '@/modules/Statements/services/bankStatementRoutes';
import EthicQuizRoutes from '@/modules/Ethics/services/ethicRoutes';
import processProxy from '@/env/processProxy';
import GoToService from './GoToService';

const BankSecrecy = () => import(/* webpackChunkName: "BankSecrecy" */ '@/modules/bankSecrecy/BankSecrecy.vue');
const MultiformEngineEditPreview = () => import(/* webpackChunkName: "Multiform" */ '@/modules/multiForm/engine/views/MultiformEngineEditPreview.vue');
const MultiformEngineTest = () => import(/* webpackChunkName: "Multiform" */ '@/modules/multiForm/engine/views/MultiformEngineTestPanel.vue');
const MultiformEnginePrint = () => import(/* webpackChunkName: "Multiform" */ '@/modules/multiForm/engine/views/MultiformEnginePrint.vue');
const MultiformTestPanel = () => import(/* webpackChunkName: "Multiform" */ '@/modules/multiForm/engine/MultiformTestPanel.vue');

// const Livespace = () => import(/* webpackChunkName: "Calculator" */ '@/components/livespace/Livespace.vue');
const Dashboard = () => import(/* webpackChunkName: "Dashboard" */ '@/modules/dashboard/Dashboard.vue');
const ErrorPage = () => import(/* webpackChunkName: "ErrorPage" */ '@/components/ErrorPage.vue');
const ZitadelCallback = () => import(/* webpackChunkName: "ZitadelCallback" */ '@/components/ZitadelCallback.vue');
const NotFoundPage = () => import(/* webpackChunkName: "NotFoundPage" */ '@/components/NotFoundPage.vue');
const UserCalendar = () => import(/* webpackChunkName: "UserCalendar" */ '@/modules/calendar/UserCalendar.vue');
const Login = () => import(/* webpackChunkName: "Login" */ '@/modules/user/passwordManagement/Login.vue');
const MfaLogin = () => import(/* webpackChunkName: "MfaLogin" */ '@/modules/user/passwordManagement/LoginMfa.vue');
const MfaLogout = () => import(/* webpackChunkName: "MfaLogout" */ '@/modules/user/passwordManagement/LogoutMfa.vue');
const Register = () => import(/* webpackChunkName: "Register" */ '@/modules/user/Register.vue');
const MeetTeam = () => import(/* webpackChunkName: "MeetTeam" */ '@/modules/meetTeam/view/MeetTeam.vue');
const UserEditPanel = () => import(/* webpackChunkName: "UserEditPanel" */ '@/modules/user/editUser/UserEditPanel.vue');
const LendiBegin = () => import(/* webpackChunkName: "LendiBegin" */ '@/modules/lendiBegin/LendiBegin.vue');
const BikReportOldView = () => import(/* webpackChunkName: "BikReportOldView" */ '@/modules/client/views/bik-report/BikOldView.vue');
const PasswordChange = () => import(/* webpackChunkName: "PasswordChange" */ '@/modules/user/passwordManagement/PasswordChange.vue');
const PasswordReset = () => import(/* webpackChunkName: "PasswordReset" */ '@/modules/user/passwordManagement/PasswordReset.vue');
const MyCalendar = () => import(/* webpackChunkName: "MyCalendar" */ '@/modules/calendar/MyCalendar.vue');
const BlockedAccess = () => import(/* webpackChunkName: "BlockedAccess" */ '@/modules/user/blockedAccess/BlockedAccess.vue');
const ClientMultiform = () => import(/* webpackChunkName: "ClientMultiform" */ '@/modules/multiForm/views/ClientMultiform.vue');
const ClientAccessBlocked = () => import(/* webpackChunkName: "ClientAccessBlocked" */ '@/modules/client/views/ClientAccessBlocked.vue');
const LoanApplicationsCardList = () => import(/* webpackChunkName: "LoanApplicationsCardList" */ '@/modules/loanApplication/views/LoanApplicationsCardList.vue');
const ClientChecklist = () => import(/* webpackChunkName: "ClientChecklist" */ '../modules/client/views/client-checklist/ClientChecklist.vue');
const DiscountsComponent = () => import(/* webpackChunkName: "Discounts" */ '@/modules/discounts/DiscountsComponent.vue');
const BankBranchesPanel = () => import(/* webpackChunkName: "BankBranchesPanel" */ '@/modules/adminPanel/views/BankBranchesPanel.vue');
const FinancialInstitutionsList = () => import(/* webpackChunkName: "FinancialInstitutionsList" */ '@/modules/banks/FinancialInstitutionsList.vue');
const ProductsPanel = () => import(/* webpackChunkName: "ProductsPanel" */ '@/modules/products/ProductsPanel.vue');
const BigRaporstAgreements = () => import(/* webpackChunkName: "BigRaportsAgreements" */ '@/modules/adminPanel/views/BigRaportsAgreements.vue');
const SmsLogsPanel = () => import(/* webpackChunkName: "SmsLogsPanel" */ '@/modules/adminPanel/views/SmsLogsPanel.vue');
const SmsLogsPanelV3 = () => import(/* webpackChunkName: "SmsLogsPanel" */ '@/modules/adminPanel/views/SmsLogsPanelV3.vue');
const EmailLogsPanelV3 = () => import(/* webpackChunkName: "EmailLogsPanel" */ '@/modules/adminPanel/views/EmailLogsPanelV3.vue');
const ProspectSettings = () => import(/* webpackChunkName: "SmsLogsPanel" */ '@/modules/adminPanel/views/ProspectSettings.vue');
const HRLock = () => import(/* webpackChunkName: "HRLock" */ '@/modules/user/blockedAccess/HRLock.vue');
const EmailLogsPanel = () => import(/* webpackChunkName: "EmailLogsPanel" */ '@/modules/adminPanel/views/EmailLogsPanel.vue');
const Asystent360 = () => import(/* webpackChunkName: "AI" */ '@/modules/ai/asystent360.vue');
const AiChat = () => import(/* webpackChunkName: "AI" */ '@/modules/ai/chat.vue');
const AiOwuChat = () => import(/* webpackChunkName: "AI" */ '@/modules/ai/owu.vue');
const AiAgentsChat = () => import(/* webpackChunkName: "AI" */ '@/modules/ai/agents.vue');
const AiCcMenager = () => import(/* webpackChunkName: "AI" */ '@/modules/ai/cc-menager.vue');
const AiIssuesChat = () => import(/* webpackChunkName: "AI" */ '@/modules/ai/issues.vue');
const OffersPrintout = () => import(/* webpackChunkName: "OffersPrintout" */ '@/modules/calculator/views/OffersPrintout.vue');
const OffersPrintoutV25 = () => import(/* webpackChunkName: "OffersPrintoutV25" */ '@/modules/calculator/views/OffersPrintoutV25.vue');
const SchedulePrintout = () => import(/* webpackChunkName: "SchedulePrintout" */ '@/modules/calculator/views/SchedulePrintout.vue');
const DesignPage = () => import(/* webpackChunkName: "DesignPage" */ '@/components/DesignPage.vue');
const DesignBaseColorsPage = () => import(/* webpackChunkName: "DesignBaseColorsPage" */ '@/components/design/DesignBaseColorsPage.vue');
const KsefList = () => import(/* webpackChunkName: "KsefList" */ '@/modules/adminPanel/views/KsefList.vue');
const CookieTest = () => import(/* webpackChunkName: "CookieTest" */ '@/modules/test/CookieTest.vue');
const TeleconferencePage = () => import(/* webpackChunkName: "MeetPage" */ '@/components/meet/TeleconferencePage.vue');
const MeetPage = () => import(/* webpackChunkName: "MeetPage" */ '@/components/meet/meetPage.vue');
const ProvisionPrint = () => import(/* webpackChunkName: "Provisions" */ '@/modules/provisions/views/print/ProvisionTablePrint.vue');
const RealEstatesEntryClient = () => import('@/modules/client/views/real-estate-offers/realEstatesEntry.vue');
const RealEstateOffers = () => import('@/modules/client/views/real-estate-offers/authenticated/realEstateOffersPage.vue');
const ClientOfferDetailsPage = () => import('@/modules/client/views/real-estate-offers/authenticated/offer-details/clientOfferDetailsPage.vue');
const RealEstatesOfferDetail = () => import('@/modules/RealEstatesListing/view/withStore/RealEstatesOfferDetail.vue'
);
const RealEstatesOfferDetailDeal = () => import('@/modules/RealEstatesListing/view/withDeal/RealEstatesOfferDetail.vue');
const RealEstatesWithDeal = () => import('@/modules/RealEstatesListing/view/withDeal/RealEstatesListingPage.vue');
const RealEstatesDealEntry = () => import('@/modules/RealEstatesListing/view/withDeal/RealEstatesEntry.vue');
const RealEstatesEntry = () => import('@/modules/RealEstatesListing/view/withStore/RealEstatesEntry.vue');
const RealEstatesListingWithStore = () => import('@/modules/RealEstatesListing/view/withStore/RealEstatesListingPage.vue');
const ClientDashboard = () => import('@/modules/client/views/client-panel/ClientDashboard.vue');
const LegalRequestExperts = () => import(/* webpackChunkName: "LegalRequest" */ '@/modules/legalRequest/views/LegalRequestExperts.vue');
const LegalRequestCentral = () => import(/* webpackChunkName: "LegalRequest" */ '@/modules/legalRequest/views/LegalRequestCentral.vue');
const MyExpenses = () => import(/* webpackChunkName: "MyExpenses" */ '@/modules/myExpenses/views/MyExpenses.vue');
const DivisionReports = () => import(/* webpackChunkName: "DivisionReports" */ '@/modules/divisionReports/views/DivisionReports.vue');

export const TABLE_ID_KEY = 'tableUuid' as const;
const ZITADEL_PASSWORD_CHANGE_URL = processProxy.VUE_APP_ZITADEL_PASSWORD_CHANGE_URL;

export function getIndexRouteName() {
  return isExternalCCAgent() ? Routes.PROSPECT.LIST : Routes.DASHBOARD;
}

function getIndexRoutePath() {
  return isExternalCCAgent() ? 'prospects' : Routes.DASHBOARD;
}

function isExternalCCAgent() {
  return AuthService.User?.isExternalCC;
}

function redirectIfAuthenticated(to: any, from: any, next: any) {
  const isAuthenticated = AuthService.isAuthenticatedForRoute;
  if (isAuthenticated) {
    next({ name: getIndexRouteName() });
  } else {
    next();
  }
}

function redirectToMfaLoginIfEnabled(to: Route, from: Route, next: Function) {
  if (EnvironmentService.Environment.shouldRedirectLoginToMfa()) {
    next({
      path: '/mfa/login',
      query: to.query,
      hash: to.hash,
    });
    return;
  }

  redirectIfAuthenticated(to, from, next);
}

// Redirect authenticated users away from guest routes
async function redirectIfClientAuthenticated(to: Route, from: Route, next: Function) {
  if (ClientAuthService.isTokenValid) {
    try {
      if (!ClientAuthService.User) {
        await ClientAuthService.fetchUser();
      }
      // Redirect based on regulations/owner status
      if (ClientAuthService.needsRestricted) {
        next({ name: Routes.CLIENT_USER.RESTRICTED_DASHBOARD });
      } else {
        next({ name: Routes.CLIENT_USER.CLIENT_DASHBOARD });
      }
    } catch (e) {
      console.error('Error during client auth redirect:', e);
      // If fetching fails, stay on guest route
      next();
    }
  } else {
    next();
  }
}

// Require auth but NOT regulations (for restricted route)
async function requireAuthWithoutRegulations(to: Route, from: Route, next: Function) {
  if (!ClientAuthService.isTokenValid) {
    next({ name: Routes.CLIENT_USER.GUEST_DASHBOARD, query: { redirect: to.fullPath } });
    return;
  }
  try {
    if (!ClientAuthService.User) {
      await ClientAuthService.fetchUser();
    }
    await DictionaryStore.fetchAllDictionaries()
    // If fully set up, redirect to full access
    if (!ClientAuthService.needsRestricted) {
      next({ name: Routes.CLIENT_USER.CLIENT_DASHBOARD });
    } else {
      next();
    }
  } catch (e) {
    console.error('Error in requireAuthWithoutRegulations:', e);
    next({ name: Routes.CLIENT_USER.GUEST_DASHBOARD });
  }
}

function redirectToNewRealEstatePanel(to: Route, from: Route, next: Function) {
  ClientAuthService.goToNewRealEstatePanel();
}

// Require auth AND regulations (for full access)
async function requireFullAuth(to: Route, from: Route, next: Function) {
  if (!ClientAuthService.isTokenValid) {
    next({ name: Routes.CLIENT_USER.GUEST_DASHBOARD, query: { redirect: to.name !== Routes.CLIENT_USER.CLIENT_DASHBOARD ? to.fullPath : undefined } });
    return;
  }
  try {
    if (!ClientAuthService.User) {
      await ClientAuthService.fetchUser();
    }
    await DictionaryStore.fetchAllDictionaries();
    if (ClientAuthService.needsRestricted) {
      next({ name: Routes.CLIENT_USER.RESTRICTED_DASHBOARD });
    } else {
      next();
    }
  } catch (e) {
    console.error('Error in requireFullAuth:', e);
    next({ name: Routes.CLIENT_USER.GUEST_DASHBOARD });
  }
}

Vue.use(Router);

const AppRoutes: RouteConfig[] = [
  {
    path: '/',
    redirect: { name: getIndexRouteName() },
  },
  {
    name: Routes.DASHBOARD,
    path: Routes.DASHBOARD,
    component: Dashboard,
    meta: {
      title: () => I18NGetter().PlatformSection.dashboard,
      icon: 'mdi-view-dashboard-outline',
      mode: 'lendi',
      key: DashboardKey.DASHBOARD,
      condition: () => !isExternalCCAgent(),
    },
  },
  {
    path: 'calendar',
    name: Routes.ACTIVITIES.CALENDAR,
    meta: {
      title: () => I18NGetter().PlatformSection.calendar,
      icon: 'mdi-calendar-outline',
      mode: 'crm',
      key: DashboardKey.CALENDAR,
      module: CrmModules.CALENDAR,
      condition: () => AuthService.User!.canAccessModule.CALENDAR && !AuthService.User!.isLendiCareChiefWithoutAccessToLoans,
    },
    component: MyCalendar,
  },
  {
    path: 'clients',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: ClientRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.clients,
      icon: 'mdi-account-group-outline',
      mode: 'crm',
      key: DashboardKey.CLIENTS,
      condition: () => AuthService.User!.canAccessModule.CLIENTS && !AuthService.User!.isLendiCareChiefWithoutAccessToLoans,
    },
  },
  {
    path: 'deals',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: DealsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.deals,
      icon: 'mdi-briefcase-outline',
      mode: 'crm',
      key: DashboardKey.DEALS,
      condition: () => AuthService.User!.canAccessModule.DEALS && !AuthService.User!.isLendiCareChiefWithoutAccessToLoans,
    },
  },
  {
    path: 'reports',
    name: Routes.DIVISION_REPORTS,
    component: DivisionReports,
    // Holds the route until lendi-ai answers. `condition` below starts false,
    // and `checkRouteAccess` reads it in the router's afterEach — so without
    // the wait a director opening this from a bookmark would be pushed back
    // to the dashboard before the verdict ever arrived.
    beforeEnter: divisionReportsGuard,
    meta: {
      title: () => I18NGetter().PlatformSection.division_reports,
      icon: 'mdi-chart-box-outline',
      mode: 'crm',
      condition: () => canAccessDivisionReports(),
      badge: {
        is: true,
        text: 'NEW',
      },
    },
  },
  {
    path: 'loan-application',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: LoanApplicationsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.loan_application,
      icon: 'mdi-file-document-outline',
      mode: 'crm',
      key: DashboardKey.LOAN_APPLICATIONS,
      module: CrmModules.LOAN_APPLICATIONS,
      condition: () => AuthService.User!.canAccessModule.LOAN_APPLICATIONS && !AuthService.User!.isLendiCareChiefWithoutAccessToLoans,
    },
  },
  {
    path: 'calendar/:user_id',
    name: Routes.ACTIVITIES.USER_CALENDAR,
    component: UserCalendar,
  },
  /*
  {
    path: Routes.STATISTIC,
    component: Statistics,
    meta: {
      title: 'Statystyki',
      icon: 'mdi-chart-line',
      mode: 'crm',
    },
  }, */
  {
    path: 'report',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: ReportRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.report,
      icon: 'mdi-circle-multiple-outline',
      mode: 'crm',
      module: CrmModules.REPORTS,
      key: DashboardKey.SETTLEMENTS,
      condition: () =>
        AuthService.User!.canAccessModule.REPORTS &&
        AuthService.User!.privileges.hasAccessToSettlementView &&
        !AuthService.User!.isLendiCareChiefWithoutAccessToLoans,
    },
  },
  {
    path: 'prospects',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: ProspectsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.prospects,
      icon: 'mdi-sprout',
      mode: 'crm',
      module: CrmModules.PROSPECTS,
      condition: () => AuthService.User!.privileges.isAgentCC && EnvironmentService.Environment.isProspectsOn,
    },
  },
  {
    path: 'partners',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: PartnerRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.partners,
      icon: 'mdi-handshake-outline',
      mode: 'crm',
      key: DashboardKey.PARTNERS,
      module: CrmModules.PARTNERS,
      condition: () => AuthService.User!.canAccessModule.PARTNERS,
    },
  },
  {
    path: 'branches',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: BranchesRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.branches,
      icon: 'mdi-domain',
      mode: 'admin',
      condition: () => AuthService.User!.isAdmin ||
        (AuthService.User!.isVerifier && AuthService.User!.privileges.canManageOrganisation),
    },
  },
  {
    path: 'asystent-360',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.AI.ASYSTENT_360,
        component: Asystent360,
      },
    ],
    meta: {
      customIcon: 'asystent360',
      title: 'Asystent 360',
      mode: 'lendi',
      module: CrmModules.AI,
      condition: () => AuthService.User!.canAccessModule.AI && EnvironmentService.Environment.isAppDomain().LENDI_PL,
    },
  },
  ...CalculatorRoutes,
  {
    path: 'real-estates',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        component: RealEstatesEntry,
        children: [
          {
            path: '',
            name: Routes.REAL_ESTATES_LISTING.REAL_ESTATE_OFFERS,
            component: RealEstatesListingWithStore,
          },
          {
            path: 'details/:offerId',
            name: Routes.REAL_ESTATES_LISTING.REAL_ESTATE_OFFER_DETAILS_PAGE_OFFER,
            component: RealEstatesOfferDetail,
          },
        ],
      },
      {
        path: 'deal/:dealId',
        component: RealEstatesDealEntry,
        children: [
          {
            path: '',
            name: Routes.REAL_ESTATES_LISTING.REAL_ESTATE_OFFERS_DEAL,
            component: RealEstatesWithDeal,
          },
          {
            path: 'details/:offerId',
            name: Routes.REAL_ESTATES_LISTING.REAL_ESTATE_OFFER_DETAILS_PAGE_OFFER_DEAL,
            component: RealEstatesOfferDetailDeal,
          },
        ],
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.real_estates,
      icon: 'mdi-home-outline',
      mode: 'lendi',
      key: DashboardKey.REAL_ESTATES,
      hideNavigationBar: true,
      module: CrmModules.REAL_ESTATES,
      minNavigation: true,
      condition: () => AuthService.User!.canAccessModule.REAL_ESTATES,
    },
  },
  {
    path: 'ai-chat',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.AI.CHAT,
        component: AiChat,
      },
    ],
    meta: {
      icon: 'mdi-chat-outline',
      title: 'Chat',
      mode: 'lendi',
      module: CrmModules.AI,
      condition: () => AuthService.User!.canAccessModule.AI && EnvironmentService.Environment.isAppDomain().LENDI_PL && (AuthService.User!.isAdminOrVerifier || AuthService.User!.isCoordinator) && AuthService.User!.canAccessModule.KNOWLEDGE,
      badge: {
        is: true,
        text: 'AI',
        color: 'purple',
      },
    },
  },
  {
    path: 'knowledge',
    meta: {
      title: () => I18NGetter().PlatformSection.knowledge,
      icon: 'mdi-library',
      mode: 'lendi',
      key: DashboardKey.KNOWLEDGE_BASE,
      module: CrmModules.KNOWLEDGE,
      condition: () => AuthService.User!.canAccessModule.KNOWLEDGE,
    },
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      ...RegulationRoutes,
      ...IssuesRoutes,
      {
        path: 'issues-ai',
        name: Routes.AI.ISSUES,
        component: AiIssuesChat,
        meta: {
          icon: 'mdi-bank',
          title: 'Wiedza Produktowa',
          condition: () => EnvironmentService.Environment.isAppDomain().LENDI_PL,
          badge: {
            is: true,
            text: 'AI',
            color: 'purple',
          },
        },
      },
      ...TrainingsRoutes,
      ...FilemanagerRoutes,
      ...ProceduresRoutes,
      {
        path: 'owu',
        name: Routes.AI.OWU,
        component: AiOwuChat,
        meta: {
          icon: 'mdi-file-document-outline',
          title: 'Asystent OWU',
          condition: () => EnvironmentService.Environment.isAppDomain().LENDI_PL,
          badge: {
            is: true,
            text: 'AI',
            color: 'purple',
          },
        },
      },
    ],
  },
  FilemanagerFileRoute,
  {
    path: 'bik',
    component: BikReportOldView,
    meta: {
      title: () => I18NGetter().PlatformSection.bik,
      icon: 'mdi-speedometer',
      mode: 'lendi',
      key: BikKey.BIK_NAVIGATION,
      module: CrmModules.BIK,
      condition: () => AuthService.User!.canAccessModule.BIK,
    },
  },
  {
    path: 'promotional-coupons',
    name: 'Discounts',
    component: DiscountsComponent,
    meta: {
      title: I18NGetter().PlatformSection.discounts,
      icon: 'mdi-ticket-percent-outline',
      mode: 'lendi',
      condition: () => AuthService.User!.canAccessModule.DISCOUNTS,
    },
  },
  {
    path: 'lendi-begin',
    meta: {
      title: () => I18NGetter().PlatformSection.lendi_begin,
      icon: 'mdi-flag-checkered',
      module: CrmModules.LENDI_BEGININGS,
      mode: 'about',
      condition: () => AuthService.User!.canAccessModule.LENDI_BEGININGS,
    },
    component: LendiBegin,
  },
  {
    path: 'widgets',
    meta: {
      title: () => I18NGetter().PlatformSection.widgets,
      icon: 'mdi-widgets-outline',
      mode: 'about',
      minNavigation: true,
      module: CrmModules.WIDGETS,
      condition: () => !AuthService.User?.isHomeEstateUser,
    },
    component: {
      template: EmptyTemplateWithRouterView(true),
    },
    children: WidgetRoutes,
  },
  {
    path: 'divisions',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: DivisionRoutes,
  },
  {
    path: 'livespace',
    meta: {
      title: 'Livespace',
      icon: 'mdi-desktop-classic',
      mode: 'admin',
      module: CrmModules.LIVESPACE,
      minNavigation: true,
      condition: () => {
        return AuthService.User!.isAdmin || AuthService.User!.isVerifier;
      },
    },
    component: {
      template: EmptyTemplateWithRouterView(true),
    },
    children: LivespaceRoutes,
  },
  {
    path: 'about',
    name: Routes.ABOUT,
    component: MeetTeam,
    meta: {
      title: () => EnvironmentService.Environment.isAppDomain().LENDI_PL ? I18NGetter().PlatformSection.lendi_team : I18NGetter().PlatformSection.about,
      icon: 'mdi-account-group',
      mode: 'about',
      key: DashboardKey.LENDI_TEAM,
      module: CrmModules.LENDI_CREW,
      condition: () => AuthService.User!.canAccessModule.LENDI_CREW,
    },
  },
  {
    path: 'accreditation_panel',
    name: 'AccreditationPanel',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: AccreditationPanelRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.accreditation_panel,
      icon: 'mdi-check-decagram-outline',
      mode: 'about',
      key: DashboardKey.ACCREDITATION_PANEL,
      module: CrmModules.ACCREDITATION_PANEL,
      condition: () => AuthService.User!.canAccessModule.ACCREDITATION_PANEL,
      badge: {
        is: true,
        text: 'New',
        // Znacznik „New" dotyczy oświadczenia UniCredit, więc widzą go tylko ci, którzy
        // faktycznie dostaną kafelek — ten sam predykat obsługuje kafel i dymek Lendy.
        condition: () => oneOffStatementsConfig[QuizCategory.UNI_CREDIT].isAvailable(AuthService.User),
      },
    },
  },
  {
    path: 'discussions',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: DiscussionsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.discussions,
      icon: 'mdi-forum',
      mode: 'about',
      module: CrmModules.DISCUSSIONS,
      key: DashboardKey.FORUM,
      condition: () => AuthService.User!.canAccessModule.DISCUSSIONS &&
        !AuthService.User!.isLendiCareChiefWithoutAccessToLoans &&
        (!AuthService.User!.isLendiCareUser || (AuthService.User!.isLendiCareUser && AuthService.User!.hasAccessToLoans)),
    },
  },
  {
    path: 'legal-request-experts',
    name: Routes.LEGAL_REQUEST.EXPERTS,
    component: LegalRequestExperts,
    meta: {
      title: () => I18NGetter().PlatformSection.legal_request_experts,
      icon: 'mdi-comment-alert-outline',
      mode: 'about',
      condition: () => !(AuthService.User!.isAdminOrVerifier || AuthService.User!.isCoordinator) && EnvironmentService.Environment.isAppDomain().LENDI_PL,
    },
  },
  {
    path: 'legal-request-central',
    name: Routes.LEGAL_REQUEST.CENTRAL,
    component: LegalRequestCentral,
    meta: {
      title: () => I18NGetter().PlatformSection.legal_request_central,
      icon: 'mdi-comment-alert-outline',
      mode: 'about',
      condition: () => (AuthService.User!.isAdminOrVerifier || AuthService.User!.isCoordinator) && EnvironmentService.Environment.isAppDomain().LENDI_PL,
    },
  },
  {
    path: 'my-expenses',
    name: Routes.MY_EXPENSES,
    component: MyExpenses,
    meta: {
      title: () => I18NGetter().PlatformSection.my_expenses,
      icon: 'mdi-cash-multiple',
      mode: 'about',
      condition: () => EnvironmentService.Environment.isAppDomain().LENDI_PL,
    },
  },
  {
    path: 'verification',
    name: 'Verification',
    meta: {
      title: () => I18NGetter().PlatformSection.verification_panel,
      icon: 'mdi-folder-search-outline',
      mode: 'admin',
      condition: () => AuthService.User?.isVerifier,
    },
  },
  {
    path: 'ksef',
    name: Routes.ADMIN.KSEF,
    component: KsefList,
    meta: {
      title: () => I18NGetter().PlatformSection.ksef,
      icon: 'mdi-file-document-multiple-outline',
      mode: 'admin',
      condition: () => AuthService.User!.isAdminOrVerifier && EnvironmentService.Environment.isAppDomain().LENDI_PL,
    },
  },
  {
    path: 'provisions-table',
    component: {
      template: EmptyTemplateWithRouterView(true),
    },
    children: ProvisionsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.provisions,
      icon: 'mdi-currency-usd',
      mode: 'admin',
      minNavigation: true,
      condition: () => AuthService.User?.privileges.canEditProvisions,
    },
  },
  {
    path: 'provisions',
    name: 'provisions',
    meta: {
      title: () => I18NGetter().PlatformSection.commissions,
      icon: 'mdi-currency-usd',
      mode: 'admin',
      condition: () => AuthService.User?.privileges.canEditProvisions,
      module: CrmModules.OLD_COMMISSIONS,
      action: () => {
        window.open(`${processProxy.VUE_APP_SIMULATION_URL}provisions`, '_blank');
      },
    },
  },
  {
    path: 'product-panel',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    meta: {
      title: () => 'Panel produktowy',
      icon: 'mdi-toy-brick-search',
      mode: 'admin',
      module: CrmModules.USER_MANAGEMENT,
      action: () => {
        GoToService.lendiV3('app/product-panel/productTypeMortgage/dashboard/products');
      },
      condition: () => AuthService.User?.canAccessModule.USER_MANAGEMENT &&
        EnvironmentService.Environment.isAppDomain().LENDI_PL,
    },
  },
  {
    path: 'users-management',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: AdminUserPanelRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.users_managments,
      icon: 'mdi-account-tie-outline',
      mode: 'admin',
      module: CrmModules.USER_MANAGEMENT,
      condition: () => AuthService.User?.canAccessModule.USER_MANAGEMENT ||
        (AuthService.User?.privileges.isAgentCC && AuthService.User?.isUserOf.LENDI),
    },
  },
  {
    path: 'divisions-management',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: AdminDivisionPanelRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.divisions_management,
      icon: 'mdi-account-group-outline',
      mode: 'admin',
      condition: () => AuthService.User && (AuthService.User.isAdmin || AuthService.User.isVerifier),
    },
  },
  {
    path: 'nps-management',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: NPSRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.nps,
      icon: 'mdi-message-draw',
      mode: 'admin',
      module: CrmModules.NPS,
      condition: () => AuthService.User!.canAccessModule.NPS,
    },
  },
  {
    path: 'human-resources',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: HumanResourcesRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.human_resources,
      icon: 'mdi-list-status',
      mode: 'admin',
      module: CrmModules.HUMAN_RESOURCES,
      condition: () => AuthService.User!.canAccessModule.HUMAN_RESOURCES,
    },
  },
  {
    path: 'delegation-lists',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: DelegationsListsRoutes,
    meta: {
      title: () => I18NGetter().PlatformSection.delegations_lists,
      icon: 'mdi-format-list-bulleted',
      mode: 'admin',
      module: CrmModules.DELEGATION_LIST,
      condition: () => AuthService.User!.canAccessModule.DELEGATION_LIST,
    },
  },
  {
    path: 'management/bank/branches',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.ADMIN.BANK_BRANCHES_LIST,
        component: BankBranchesPanel,
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.bank_branches,
      icon: 'mdi-bank-plus',
      mode: 'admin',
      module: CrmModules.BANK_BRANCHES,
      condition: () => AuthService.User && (AuthService.User.isAdmin || (AuthService.User.isVerifier)),
    },
  },
  {
    path: 'management/products',
    name: Routes.ADMIN.PRODUCTS,
    component: ProductsPanel,
    meta: {
      title: () => I18NGetter().PlatformSection.products,
      icon: 'mdi-package-variant-closed',
      mode: 'admin',
      module: CrmModules.PRODUCTS,
      condition: () => AuthService.User?.isAdmin,
    },
  },
  {
    path: 'management/banks',
    name: Routes.ADMIN.FINANCIAL_INSTITUTION_LIST,
    component: FinancialInstitutionsList,
    meta: {
      title: () => I18NGetter().PlatformSection.banks,
      icon: 'mdi-bank',
      mode: 'admin',
      condition: () => AuthService.User?.canManageFinancialInstitutions,
    },
  },
  {
    path: 'management/approvals/big',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.ADMIN.BIG_RAPORTS_AGREEMENTS,
        component: BigRaporstAgreements,
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.approvals_bik,
      icon: 'mdi-clipboard-check',
      mode: 'admin',
      module: CrmModules.BIG_AGREEMENTS,
      condition: () => AuthService.User!.canAccessModule.BIG_AGREEMENTS,
    },
  },
  {
    path: 'management/notifications',

    meta: {
      title: () => I18NGetter().PlatformSection.notifications,
      icon: 'mdi-message-processing',
      mode: 'admin',
      condition: () => AuthService.User!.isAdminOrVerifier,
    },
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: 'sms',
        name: Routes.ADMIN.SMS_LIST_NEW,
        component: SmsLogsPanelV3,
        meta: {
          title: I18NGetter().PlatformSection.sms_logs,
          icon: 'mdi-message-processing',
          condition: () => AuthService.User!.isAdminOrVerifier,
          badge: {
            is: true,
            text: 'NEW',
          },
        },
      },
      {
        path: 'email',
        name: Routes.ADMIN.EMAIL_LIST_NEW,
        component: EmailLogsPanelV3,
        meta: {
          title: I18NGetter().PlatformSection.email_logs,
          icon: 'mdi-mail',
          condition: () => AuthService.User!.isAdminOrVerifier,
          badge: {
            is: true,
            text: 'NEW',
          },
        },
      },
      {
        path: 'sms/logs',
        name: Routes.ADMIN.SMS_LIST,
        component: SmsLogsPanel,
        meta: {
          title: I18NGetter().PlatformSection.sms_logs,
          icon: 'mdi-message-processing',
          mode: 'admin',
          module: CrmModules.SMS_LOGS,
          condition: () => AuthService.User!.canAccessModule.SMS_LOGS,
        },
      },
      {
        path: 'email/logs',
        name: Routes.ADMIN.EMAIL_LIST,
        component: EmailLogsPanel,
        meta: {
          title: I18NGetter().PlatformSection.email_logs,
          icon: 'mdi-mail',
          mode: 'admin',
          module: CrmModules.EMAIL_LOGS,
          condition: () => AuthService.User!.canAccessModule.EMAIL_LOGS,
        },
      },
    ],
  },
  {
    path: 'ai-agents',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.AI.AGENTS,
        component: AiAgentsChat,
      },
    ],
    meta: {
      icon: 'mdi-file-document-outline',
      title: 'Asystenci AI',
      module: CrmModules.AI,
      mode: 'admin',
      condition: () => EnvironmentService.Environment.isAppDomain().LENDI_PL && AuthService.User!.isAdminOrVerifier,
      badge: {
        is: true,
        text: 'AI',
        color: 'purple',
      },
    },
  },
  /* {
    path: 'ai-poc',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.AI.CHAT,
        component: AiChat,
        meta: {
          icon: 'mdi-chat-processing',
          title: 'Bezpieczny chat',
          condition: () => EnvironmentService.Environment.isPreprodOrDev,
        },
      },
      {
        path: 'owu',
        name: Routes.AI.OWU,
        component: AiOwuChat,
        meta: {
          icon: 'mdi-file-document-outline',
          title: 'OWU AI',
          condition: () => EnvironmentService.Environment.isPreprodOrDev,
        },
      },
      {
        path: 'issues',
        name: Routes.AI.ISSUES,
        component: AiIssuesChat,
        meta: {
          icon: 'mdi-bank',
          title: 'AI Issues',
          condition: () => EnvironmentService.Environment.isPreprodOrDev,
        },
      },
      {
        path: 'agents',
        name: Routes.AI.AGENTS,
        component: AiAgentsChat,
        meta: {
          icon: 'mdi-account-tie-voice',
          title: 'AI Agenci',
          condition: () => EnvironmentService.Environment.isPreprodOrDev,
        },
      },
      {
        path: 'cc-menager',
        name: Routes.AI.CC_MENAGER,
        component: AiCcMenager,
        meta: {
          icon: 'mdi-phone-outline',
          title: 'AI CC Menager',
          condition: () => EnvironmentService.Environment.isDev,
        },
      },
    ],
    meta: {
      title: () => 'AI',
      icon: 'mdi-robot',
      mode: 'admin',
      module: CrmModules.AI,
      condition: () => EnvironmentService.Environment.isAppDomain().LENDI_PL && EnvironmentService.Environment.isPreprodOrDev,
    },
  }, */
  {
    path: 'multiform',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.ADMIN.MULTIFORM_TEST_PANEL,
        component: MultiformTestPanel,
      },
      {
        path: 'engine-test/:id',
        name: Routes.ADMIN.MULTIFORM_ENGINE_TEST,
        component: MultiformEngineTest,
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.multiform,
      icon: 'mdi-file-document-multiple',
      mode: 'admin',
      condition: () => AuthService.User?.canEditModule.MULTIFORM,
    },
  },
  {
    path: 'management/prospect-settings',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.ADMIN.PROSPECT_SETTINGS,
        component: ProspectSettings,
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.prospect_settings,
      icon: 'mdi-cog',
      mode: 'admin',
      module: CrmModules.PROSPECTS_SETTINGS,
      condition: () => AuthService.User!.canAccessModule.PROSPECTS_SETTINGS,
    },
  },
  {
    path: 'management/vienna-life',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.ADMIN.VIENNA_LIFE,
        component: () => import(/* webpackChunkName: "ViennaLifePanel" */ '@/modules/adminPanel/views/ViennaLifePanel.vue'),
      },
    ],
    meta: {
      title: () => I18NGetter().PlatformSection.vienna_life,
      icon: 'mdi-shield-account-outline',
      mode: 'admin',
      condition: () => AuthService.User?.privileges.hasAccessToViennaLife,
    },
  },
  {
    path: 'management/ai-call-center-feedback',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      {
        path: '',
        name: Routes.AI.CC_MENAGER,
        component: AiCcMenager,
      },
    ],
    meta: {
      title: () => "Call Center AI",
      icon: 'mdi-cog',
      mode: 'admin',
      module: CrmModules.AI,
      condition: () => !AuthService.User?.isExternalCC && AuthService.User?.privileges.isAgentCC && EnvironmentService.Environment.isAppDomain().LENDI_PL,
      badge: {
        is: true,
        text: 'AI',
        color: 'purple',
      },
    },
  },
  {
    name: Routes.USER_EDIT,
    path: 'user/edit',
    component: UserEditPanel,
    meta: {
      title: () => I18NGetter().useUserPanel.MY_DATA,
      mode: 'user',
    },
  },
  /* {
    path: '#',
    meta: {
      title: 'Moje dokumenty',
      mode: 'user',
    },
  }, */
  {
    path: '/password-change',
    name: Routes.PASSWORD_MANAGEMENT.PASSWORD_CHANGE,
    component: Login,
    meta: {
      title: () => I18NGetter().useUserPanel.CHANGE_PASSWORD,
      mode: 'user',
      action: () => {
        /** Zitadel view handling*/
        if (AuthService.isZitadelAuthMode) {
          const loginName = AuthService.zitadelLoginName?.trim();
          if (!loginName) {
            Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useMfaPanel.LOGIN_NAME_MISSING);
            return;
          }

          const redirectUrl = window.location.href;
          const queryParams = new URLSearchParams({
            loginName,
            redirect: redirectUrl,
          });
          GoToService.thirdPartySite(
            `${ZITADEL_PASSWORD_CHANGE_URL}?${queryParams.toString()}`
          );
          return;
        }

        /** @Deprecated Old auth logic */
        AppRouter.push({
          name: Routes.PASSWORD_MANAGEMENT.PASSWORD_CHANGE,
        });
      },
    },
  },
  {
    path: 'design',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    redirect: { name: Routes.DESIGN_BASE_COLORS, },
    children: [
      {
        path: 'colors',
        name: Routes.DESIGN_BASE_COLORS,
        component: DesignBaseColorsPage,
      },
    ],
  },
  {
    path: 'test/cookie-tracker',
    name: Routes.TEST,
    component: CookieTest,
    meta: {
      title: () => 'Cookie Test',
      icon: 'mdi-test-tube',
      mode: 'admin',
      condition: () => !EnvironmentService.Environment.isProd && AuthService.User?.isAdmin,
    },
  },
];
const AppRouter = new Router({
  mode: 'history',
  base: processProxy.VUE_APP_CONTEXT_URL,
  scrollBehavior: (to, from, savedPosition) => {
    return to.name !== from.name ? { x: 0, y: 0, } : savedPosition;
  },
  routes: [
    {
      path: '/',
      redirect: `app/${getIndexRoutePath()}`,
    },
    {
      path: '/bank-secrecy',
      name: Routes.BANK_SECRECY,
      component: BankSecrecy,
    },
    {
      path: '/login',
      name: Routes.PASSWORD_MANAGEMENT.LOGIN,
      component: Login,
      beforeEnter: redirectToMfaLoginIfEnabled,
    },
    {
      path: '/mfa/login',
      name: 'mfaLogin',
      component: MfaLogin,
      beforeEnter: redirectIfAuthenticated,
    },
    {
      path: '/auth/logout/callback',
      name: 'mfaLogout',
      component: MfaLogout,
      beforeEnter: redirectIfAuthenticated,
    },
    {
      path: '/register',
      name: Routes.REGISTER,
      component: Register,
    },
    {
      path: '/password-change',
      name: Routes.PASSWORD_MANAGEMENT.PASSWORD_CHANGE,
      component: PasswordChange,
      beforeEnter: (to, from, next) => {
        if (AuthService.isAuthenticatedForRoute) {
          next();
        } else {
          AuthService.softLogout();
        }
      },
    },
    {
      path: '/password-reset',
      name: Routes.PASSWORD_MANAGEMENT.PASSWORD_RESET,
      component: PasswordReset,
    },
    {
      path: '/client',
      beforeEnter: async (from, to, next) => {
        await DictionaryStore.fetchLocationDictionaries();
        next();
      },
      component: {
        template: EmptyTemplateWithRouterView(),
      },
      children: [
        {
          path: 'login',
          redirect: {
            name: Routes.CLIENT_USER.GUEST_DASHBOARD,
          },
        },
        {
          path: 'login-with-code',
          name: Routes.CLIENT_USER.LOGIN_WITH_CODE,
          component: () => import('@/modules/client/views/client-panel/LoginWithCode.vue'),
        },
        // GUEST ROUTES - No authentication required
        {
          path: 'guest',
          component: () => import('@/modules/client/views/client-panel/guest/GuestClientNavigation.vue'),
          beforeEnter: redirectIfClientAuthenticated,
          children: [
            {
              path: '',
              redirect: { name: Routes.CLIENT_USER.GUEST_DASHBOARD },
            },
            {
              path: 'dashboard',
              name: Routes.CLIENT_USER.GUEST_DASHBOARD,
              component: () => import('@/modules/client/views/client-panel/guest/ClientDashboardGuest.vue'),
            },
            {
              path: '',
              component: () => import('@/modules/client/views/real-estate-offers/realEstatesEntry.vue'),
              children: [{
                path: 'real-estate-offers',
                name: Routes.CLIENT_USER.GUEST_REAL_ESTATE_OFFERS,
                component: () => import('@/modules/client/views/real-estate-offers/guest/GuestRealEstateOffersPage.vue'),
                beforeEnter: redirectToNewRealEstatePanel,
              },
              {
                path: 'real-estate-offers/:offerId',
                name: Routes.CLIENT_USER.GUEST_OFFER_DETAILS,
                component: () => import('@/modules/client/views/real-estate-offers/guest/offer-details/GuestOfferDetailsPage.vue'),
                meta: {
                  hideNavigation: true,
                },
                beforeEnter: redirectToNewRealEstatePanel,
              }]
            },
          ],
        },

        // RESTRICTED ROUTES - Auth required, regulations NOT accepted
        {
          path: 'restricted',
          component: () => import('@/modules/client/views/client-panel/restricted/RestrictedClientNavigation.vue'),
          beforeEnter: requireAuthWithoutRegulations,
          children: [
            {
              path: '',
              redirect: { name: Routes.CLIENT_USER.RESTRICTED_DASHBOARD },
            },
            {
              path: 'dashboard',
              name: Routes.CLIENT_USER.RESTRICTED_DASHBOARD,
              component: ClientDashboard,
            },
            {
              path: 'consultation-confirmed',
              name: Routes.CLIENT_USER.RESTRICTED_CONSULTATION_CONFIRMED,
              component: ClientDashboard,
              meta: { showConsultationConfirmedModal: true },
            },
            {
              path: '',
              component: () => import('@/modules/client/views/real-estate-offers/realEstatesEntry.vue'),
              children: [{
                path: 'real-estate-offers',
                name: Routes.CLIENT_USER.RESTRICTED_REAL_ESTATE_OFFERS,
                component: () => import('@/modules/client/views/real-estate-offers/restricted/RestrictedRealEstateOffersPage.vue'),
                beforeEnter: redirectToNewRealEstatePanel,
              },
              {
                path: 'real-estate-offers/:offerId',
                name: Routes.CLIENT_USER.RESTRICTED_OFFER_DETAILS,
                component: () => import('@/modules/client/views/real-estate-offers/restricted/offer-details/RestrictedOfferDetailsPage.vue'),
                meta: {
                  hideNavigation: true,
                },
                beforeEnter: redirectToNewRealEstatePanel,
              },]
            },

            {
              path: 'multiform/:clientId?',
              name: Routes.CLIENT_USER.RESTRICTED_MULTIFORM,
              component: ClientMultiform,
            },
            {
              path: 'access-blocked',
              name: Routes.CLIENT_USER.RESTRICTED_ACCESS_BLOCKED,
              component: ClientAccessBlocked,
            },
            {
              path: 'offers',
              name: Routes.CLIENT_USER.RESTRICTED_OFFERS,
              component: LoanApplicationsCardList,
            },
            {
              path: 'checklist',
              name: Routes.CLIENT_USER.RESTRICTED_CHECKLIST,
              component: ClientChecklist,
            },
            {
              path: 'documents',
              name: Routes.CLIENT_USER.RESTRICTED_DOCUMENTS,
            },
          ],
        },

        // AUTHENTICATED ROUTES - Full access (auth + regulations)
        {
          path: '',
          component: () => import('@/modules/client/views/client-panel/authenticated/AuthenticatedClientNavigation.vue'),
          beforeEnter: requireFullAuth,
          children: [
            {
              path: '',
              redirect: { name: Routes.CLIENT_USER.CLIENT_DASHBOARD },
            },
            {
              path: 'dashboard',
              name: Routes.CLIENT_USER.CLIENT_DASHBOARD,
              component: ClientDashboard,
            },
            {
              path: 'consultation-confirmed',
              name: Routes.CLIENT_USER.AUTHENTICATED_CONSULTATION_CONFIRMED,
              component: ClientDashboard,
              meta: { showConsultationConfirmedModal: true },
            },
            {
              path: '',
              component: RealEstatesEntryClient,
              children: [{
                path: 'real-estate-offers',
                name: Routes.CLIENT_USER.REAL_ESTATE_OFFERS,
                component: RealEstateOffers,
                beforeEnter: redirectToNewRealEstatePanel,
              },
              {
                path: 'real-estate-offers/:offerId',
                name: Routes.CLIENT_USER.REAL_ESTATE_OFFER_DETAILS,
                component: ClientOfferDetailsPage,
                meta: {
                  hideNavigation: true,
                },
                beforeEnter: redirectToNewRealEstatePanel,
              }]
            },
            {
              path: 'multiform/:clientId?',
              name: Routes.CLIENT_USER.MULTIFORM,
              component: ClientMultiform,
            },
            {
              path: 'access-blocked',
              name: Routes.CLIENT_USER.ACCESS_BLOCKED,
              component: ClientAccessBlocked,
            },
            {
              path: 'offers',
              name: Routes.CLIENT_USER.OFFERS,
              component: LoanApplicationsCardList,
            },
            {
              path: 'checklist',
              name: Routes.CLIENT_USER.CLIENT_CHECKLIST,
              component: ClientChecklist,
            },
            {
              path: 'documents',
              name: Routes.CLIENT_USER.DOCUMENTS,
            },
          ],
        },
      ],
    },
    {
      path: '/print',
      component: {
        template: EmptyTemplateWithRouterView(),
      },
      children: [
        {
          path: `provisions/:productType/:${TABLE_ID_KEY}`,
          name: Routes.PROVISION.PRINT,
          component: ProvisionPrint,
        },
        {
          path: 'schedule/:productType/:simulationId/:offerId',
          name: Routes.NEW_CALCULATOR.SCHEDULE_PRINT,
          component: SchedulePrintout,
        },
        {
          path: 'v25/:productType/:simulationId',
          name: Routes.NEW_CALCULATOR.PRINT_V25,
          component: OffersPrintoutV25,
        },
        {
          path: ':productType/:simulationId',
          name: Routes.NEW_CALCULATOR.PRINT,
          component: OffersPrintout,
        },
      ],
      beforeEnter: PrintoutBeforeEnter,
    },
    {
      path: '/multiform/engine/:id',
      name: Routes.MULTIFORM_ENGINE_PRINT,
      component: MultiformEnginePrint,
    },
    {
      path: '/multiform/engine-test/:id',
      name: Routes.MULTIFORM_ENGINE_TEST,
      component: MultiformEngineTest,
    },
    {
      path: '/multiform/engine/preview/:id',
      name: Routes.MULTIFORM_ENGINE_PREVIEW,
      component: MultiformEngineEditPreview,
    },
    {
      path: '/design',
      name: Routes.DESIGN,
      component: DesignPage,
    },
    {
      path: '/error',
      name: Routes.ERROR,
      props: true,
      beforeEnter: async (to: Route, from, next) => {
        if (!to.query.redirect) {
          next({ name: getIndexRouteName(), });
        } else {
          next();
        }
      },
      component: ErrorPage,
    },
    {
      path: '/auth/callback',
      name: 'ZitadelCallback',
      component: ZitadelCallback,
      meta: {
        hidden: true,
      }
    },
    /*     TODO: DO WYRZUCENIA
        {
          path: '/auth/mfa',
          name: 'Mfa',
          component: Mfa,
        }, */
    {
      path: '',
      component: {
        template: EmptyTemplateWithRouterView(),
      },
      beforeEnter: async (to: Route, from, next) => {
        /**@Logic for handling token from query params */
        const queryToken = to.query.authToken;
        if (queryToken && typeof queryToken === 'string') {
          AuthService.oldToken = queryToken;
          const newQuery = { ...to.query, };
          delete newQuery.authToken;
          await AppRouter.replace({
            path: to.path,
            query: newQuery,
            params: to.params,
          });
        }
        /**@EndOfLogic for handling token from query params */

        const hanndle = async (isValid: boolean, isValidUserData: any) => {
          if (isValidUserData) {
            await DictionaryStore.fetchAllDictionaries();
          }
          if (isValidUserData && AuthService.User?.requirePasswordChange && AuthService.isLegacyAuthMode) {
            next({ name: Routes.PASSWORD_MANAGEMENT.PASSWORD_CHANGE, },);
            Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useRouter.ERROR_PASSWORD_CHANGE_REQUIRED);
          } else if (isValidUserData && AuthService.User?.forceLogout) {
            await AuthService.logout();
            Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useRouter.ERROR_USER_LOGGED_OUT);
          } else if (isValid && !AuthService.User) {
            if (AuthService.isZitadelAuthMode && !AuthService.accessToken) {
              try {
                const renewed = await AuthService.renewToken();
                if (!renewed) {
                  AuthService.softLogout(to);
                  next(false);
                  return;
                }
              } catch (e) {
                Vue.prototype.$errorHandler(e, 'Token renewal failed during navigation');
                AuthService.softLogout(to);
                next(false);
                return;
              }
            }
            try {
              AuthService.User = await UserApi.userInfo();
              await DictionaryStore.fetchAllDictionaries();
              if (AuthService.User?.requirePasswordChange && AuthService.isLegacyAuthMode) {
                next({ name: Routes.PASSWORD_MANAGEMENT.PASSWORD_CHANGE, },);
                Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useRouter.ERROR_PASSWORD_CHANGE_REQUIRED);
              } else if (AuthService.User!.privileges.requireAccreditation) {
                const accsPaths = AccreditationRoutes.children?.map(x => x.name).filter(x => x !== undefined);
                if (from.name) {
                  if (accsPaths?.includes(from.name)) {
                    next();
                  }
                } else {
                  next({ name: Routes.ACCREDITATION_BASE, });
                }
              } else {
                next();
              }
            } catch (e) {
              console.error(e);
              Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useRouter.ERROR_FAILED_TO_FETCH_USER_DATA);
              next({ name: Routes.ERROR, query: { redirect: 'true', }, });
            }
          } else if (isValid) {
            next();
          } else if (AuthService.isTokenValid && AuthService.isTokenExpired()) {
            console.warn('Token invalid');
            AuthService.softLogout(to);
            next(false);
          } else {
            // Bez sesji: dla tras z linków udostępnianych na zewnątrz (np. plik z repozytorium
            // wklejony w newsie) zapamiętujemy cel, żeby po zalogowaniu na niego wrócić.
            if (to.matched.some(record => record.meta?.returnAfterLogin)) {
              AuthService.rememberRouteForLogin(to);
            }
            next({ name: Routes.PASSWORD_MANAGEMENT.LOGIN, });
          }
        }

        const isValid = AuthService.isAuthenticatedForRoute;
        const isValidUserData = isValid && AuthService.User;
        await hanndle(isValid, isValidUserData)
      },
      children: [
        {
          path: '/blocked-access',
          name: Routes.BLOCKED_ACCESS,
          component: BlockedAccess,
          beforeEnter: async (to, from, next) => {
            const user: IUser = AuthService.User!;
            if (user.privileges.isHrLocked) {
              next({ name: Routes.HR_LOCKED, });
            } else {
              next();
            }
          },
        },
        {
          path: '/hr-locked',
          name: Routes.HR_LOCKED,
          component: HRLock,
          beforeEnter: async (to, from, next) => {
            const user: IUser = AuthService.User!;
            const quiz: Quizzes | undefined = user.quiz;
            if (user.privileges.requireAccreditation && user.privileges.isHrLocked) {
              next({ name: Routes.ACCREDITATION.COMPANY_DATA, },);
            } else if (quiz?.requireOsh) {
              next({ name: Routes.BHP_TEST.START_BHP_TEST, },);
            } else if (quiz?.requireStmt) {
              next({ name: Routes.STATEMENTS_TEST.START_STATEMENTS_TEST, })
            } else if (quiz?.requireBankStmt) {
              next({ name: Routes.BANK_STMTS_QUIZ.START_BANK_STMTS_QUIZ, })
            } else if (quiz?.requireKnf) {
              next({ name: Routes.KNF_TEST.START_KNF_TEST, },);
            } else if (quiz?.requireEthic) {
              next({ name: Routes.ETHIC_QUIZZ.START_ETHIC_QUIZZ, })
            } else {
              next();
            }
          },
        },
        {
          path: '/app',
          name: 'NavigationWrapper',
          component: NavigationWrapper,
          beforeEnter: async (to, from, next) => {
            const user: IUser = AuthService.User!;
            const quiz: Quizzes | undefined = user.quiz;
            if (AuthService.isSystemBlocked) {
              next({ name: Routes.BLOCKED_ACCESS, });
              console.warn('System access blocked');
            } else if (user.privileges.requireAccreditation) {
              next({ name: Routes.ACCREDITATION.COMPANY_DATA, },);
            } else if (quiz?.requireOsh) {
              next({ name: Routes.BHP_TEST.START_BHP_TEST, },);
            } else if (user.privileges.isHrLocked) {
              next({ name: Routes.HR_LOCKED, },);
            } else {
              next();
            }
          },
          children: AppRoutes,
        },
        AccreditationRoutes,
        BhpRoutes,
        StatementRoutes,
        BankStmtRoutes,
        KnfRoutes,
        EthicQuizRoutes,
      ],
    },
    {
      path: `/${Routes.TELECONFERENCE}/:uuid`,
      name: Routes.TELECONFERENCE,
      component: TeleconferencePage,
    },
    {
      path: `/${Routes.MEET}/:roomName`,
      name: Routes.MEET,
      component: MeetPage,
    },
    {
      path: '*',
      component: NotFoundPage,
    },
  ],
});

function patchRouterMethod(router: Router, methodName: 'push' | 'replace') {
  const old = clone(router);
  router[methodName] = async function (location: any) {
    return old[methodName](location).catch((error: any) => {
      if (['NavigationDuplicated', 'NavigationCancelled',].includes(error.name)) {
        return this.currentRoute;
      }
      if (error.message.includes('Redirected when going from') && error.message.includes('via a navigation guard')) {
        return this.currentRoute;
      }
      throw error;
    });
  };
}

function getRoutesPath(routesGroup: RouteConfig[], route: Route, routesPath: RouteConfig[] = []): RouteConfig[] {
  let currentRoutesPath: RouteConfig[] = [];
  const foundRoute = routesGroup.find(item => {
    if (item.name === route.name) {
      return true;
    }

    if (item.children?.length) {
      currentRoutesPath = getRoutesPath(item.children, route, routesPath);
      return !!currentRoutesPath.length;
    } else {
      return false;
    }
  });
  return foundRoute ? [...currentRoutesPath, foundRoute,] : routesPath;
}

function checkRouteAccess(route: Route) {
  return !getRoutesPath(AppRoutes, route).some(item => typeof item.meta?.condition === 'function' && !item.meta?.condition());
}

patchRouterMethod(AppRouter, 'push');
patchRouterMethod(AppRouter, 'replace');

AppRouter.onError((error) => {
  console.warn('error', { error, });
  if (error.message.includes('chunk') && error.message.includes('failed')) {
    window.location.reload();
  }
});

AppRouter.beforeEach((to, from, next) => {
  if (EnvironmentService.Environment.isPreprodOrDev) {
    const { checkIfExcludedModule, } = useModuleGuardService();
    if (checkIfExcludedModule(to, AppRoutes)) {
      next({ name: from.name ?? getIndexRouteName(), });
    } else next();
  } else next();
});

AppRouter.afterEach((to, from) => {
  if (!checkRouteAccess(to)) {
    AppRouter.push({ name: getIndexRouteName(), }).then(() => {
      Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useMain.ERROR_NO_ACCESS);
    })
    return;
  }
  // Evaluate route-scoped platform notifications for the entered tab. Skipped for path-only
  // routes and the unauthenticated/client panel (no AuthService.User).
  if (to.name && AuthService.User) {
    void Vue.prototype.$platformNotifications.runEligible({
      scope: NotificationScope.ROUTE,
      routeName: to.name,
    });
  }
});

export default AppRouter;
export {
  AppRoutes
};
